// Shared deterministic order checks are also executed by the LOCAL preview server.
// These checks do not replace Shopify-managed catalog, Checkout Blocks or Flow controls.
import { prepareNativeWholesaleCart } from './sr-wholesale-native.js';
export const MINIMUM_ORDER = 100000;
export const MINIMUM_FRAGRANCE = 3;
export function assessOrder(lines) {
  const groups = new Map();
  const errors = [];
  let subtotal = 0, units = 0;
  if (!Array.isArray(lines)) return { subtotal, units, fragrances: 0, remaining: MINIMUM_ORDER, errors: ['Choose valid bottle quantities.'], valid: false };
  for (const line of lines) {
    if (!line || !Number.isSafeInteger(line.quantity) || line.quantity < 0) { errors.push('Use whole bottle quantities.'); continue; }
    if (!line.quantity) continue;
    const min = line.min ?? 1, increment = line.increment ?? 1, max = line.max ?? Infinity;
    if (!Number.isSafeInteger(min) || min < 1 || !Number.isSafeInteger(increment) || increment < 1 || (max !== Infinity && (!Number.isSafeInteger(max) || max < 0))) {
      errors.push(line.name + ': valid bottle quantity rules are required.'); continue;
    }
    if (line.productId == null || line.productId === '') { errors.push('A valid fragrance identity is required.'); continue; }
    if (line.available !== true || line.quantity > max) errors.push(line.name + ': quantity exceeds available stock.');
    if (line.quantity < min || line.quantity % increment !== 0) errors.push(line.name + ': follow the bottle quantity rule.');
    if (!Number.isSafeInteger(line.price) || line.price < 0 || !Number.isSafeInteger(line.price * line.quantity) || !Number.isSafeInteger(subtotal + line.price * line.quantity) || !Number.isSafeInteger(units + line.quantity)) { errors.push('A valid price and quantity total are required.'); continue; }
    const group = groups.get(line.productId) || { name: line.name, quantity: 0 };
    group.quantity += line.quantity; groups.set(line.productId, group);
    subtotal += line.price * line.quantity; units += line.quantity;
  }
  for (const group of groups.values()) if (group.quantity < MINIMUM_FRAGRANCE) errors.push(group.name + ': choose at least 3 bottles.');
  if (!groups.size) errors.push('Choose fragrances to start your order.');
  const remaining = Math.max(0, MINIMUM_ORDER - subtotal);
  if (remaining && groups.size) errors.push('The order subtotal must reach $1,000.');
  return { subtotal, units, fragrances: groups.size, remaining, errors, valid: errors.length === 0 };
}
if (typeof customElements !== 'undefined' && !customElements.get('sr-wholesale')) {
  customElements.define('sr-wholesale', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.rows = [...this.querySelectorAll('[data-trade-row]')];
      this.money = new Intl.NumberFormat(this.dataset.locale || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' });
      this.dialog = this.querySelector('dialog');
      this.status = this.querySelector('[data-trade-status]');
      this.selectedOnly = false;
      this.restore();
      this.addEventListener('click', event => this.onClick(event));
      this.addEventListener('input', event => {
        if (event.target.matches('[data-trade-search]')) this.filter();
        if (event.target.matches('[data-trade-quantity]')) {
          this.setQuantity(event.target.closest('[data-trade-row]'), Number(event.target.value));
        }
      });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-trade-quantity]')) {
          this.setQuantity(event.target.closest('[data-trade-row]'), Number(event.target.value));
        }
      });
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); });
      this.update(); this.filter();
    }
    storageKey() { return 'sr-trade-local-selection-v2:' + this.id; }
    restore() {
      // Persist only in the local simulator. Never retain an approved buyer's selection
      // in a real storefront browser when account/location context could change.
      if (!this.dataset.localCheckout) return;
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey()) || '{}');
        for (const row of this.rows) {
          const quantity = saved[row.dataset.id];
          const max = row.hasAttribute('data-max') ? Number(row.dataset.max) : Infinity;
          if (row.dataset.available === 'true' && Number.isSafeInteger(quantity) && quantity >= 0 && quantity <= max) {
            row.querySelector('input').value = quantity;
            row.querySelector('input').dataset.committed = quantity;
          }
        }
      } catch { /* Storage may be unavailable; ordering still works. */ }
    }
    quantity(row) { return Number(row.querySelector('input').dataset.committed || 0); }
    lines() { return this.rows.map(row => ({
      id: row.dataset.id, productId: row.dataset.productId, name: row.dataset.name,
      variant: row.dataset.variant, price: Number(row.dataset.price), quantity: this.quantity(row),
      max: row.hasAttribute('data-max') ? Number(row.dataset.max) : Infinity,
      min: Number(row.dataset.min) || 1, increment: Number(row.dataset.increment) || 1,
      available: row.dataset.available === 'true'
    })); }
    nextQuantity(row) {
      const quantity = this.quantity(row), increment = Number(row.dataset.increment) || 1;
      if (quantity) return quantity + increment;
      const max = row.hasAttribute('data-max') ? Number(row.dataset.max) : Infinity;
      const preferred = Math.ceil(Math.max(MINIMUM_FRAGRANCE, Number(row.dataset.min) || 1) / increment) * increment;
      // Small native limits can still contribute to the same fragrance across sizes.
      return preferred <= max ? preferred : Math.floor(max / increment) * increment;
    }
    setQuantity(row, requested) {
      if (this.busy) return;
      const input = row.querySelector('input'), max = row.hasAttribute('data-max') ? Number(row.dataset.max) : Infinity;
      const increment = Number(row.dataset.increment) || 1, min = Number(row.dataset.min) || 1;
      if (!Number.isSafeInteger(requested) || requested < 0 || requested > max || (requested && (row.dataset.available !== 'true' || requested < min || requested % increment !== 0))) {
        input.value = this.quantity(row);
        input.setAttribute('aria-invalid', 'true');
        this.status.textContent = row.dataset.name + ': enter a whole bottle quantity within available stock and quantity rules.';
        this.update(); return;
      }
      input.dataset.committed = requested; input.value = requested; input.removeAttribute('aria-invalid');
      this.status.textContent = '';
      this.update(); this.filter();
    }
    filter() {
      const search = this.querySelector('[data-trade-search]').value.trim().toLocaleLowerCase();
      let count = 0;
      for (const row of this.rows) {
        row.hidden = !row.dataset.search.includes(search) || (this.selectedOnly && !this.quantity(row));
        if (!row.hidden) count++;
      }
      this.querySelector('[data-results-count]').textContent = count + ' of ' + this.rows.length + ' bottles';
      this.querySelector('[data-trade-empty]').hidden = count > 0;
      this.querySelector('[data-selected-filter]').setAttribute('aria-pressed', String(this.selectedOnly));
    }
    update() {
      const state = assessOrder(this.lines());
      for (const row of this.rows) {
        const quantity = this.quantity(row), max = row.hasAttribute('data-max') ? Number(row.dataset.max) : Infinity;
        const min = Number(row.dataset.min) || 1, increment = Number(row.dataset.increment) || 1;
        const insufficient = row.dataset.available !== 'true' || max < Math.ceil(min / increment) * increment;
        row.classList.toggle('sr-trade-row--selected', quantity > 0);
        row.classList.toggle('sr-trade-row--unavailable', insufficient);
        row.querySelector('[data-line-total]').textContent = quantity ? this.money.format(quantity * Number(row.dataset.price) / 100) : '—';
        row.querySelector('[data-quantity-plus]').disabled = this.busy || insufficient || this.nextQuantity(row) > max;
        row.querySelector('[data-quantity-minus]').disabled = this.busy || !quantity;
        row.querySelector('input').disabled = this.busy || insufficient;
        row.querySelector('[data-row-hint]').textContent = insufficient ? 'Unavailable within quantity rules' : 'Min. 3 per fragrance' + (min > 1 || increment > 1 ? ' · variant min. ' + min + ', increment ' + increment : '');
      }
      this.querySelector('[data-order-total]').textContent = this.money.format(state.subtotal / 100);
      this.querySelector('[data-mobile-total]').textContent = this.money.format(state.subtotal / 100);
      this.querySelector('[data-mobile-count]').textContent = state.units + (state.units === 1 ? ' bottle' : ' bottles');
      this.querySelector('.sr-trade-order__counts').textContent = state.fragrances + (state.fragrances === 1 ? ' fragrance · ' : ' fragrances · ') + state.units + (state.units === 1 ? ' bottle' : ' bottles');
      this.querySelector('[data-selected-badge]').textContent = state.fragrances;
      this.querySelector('[data-order-progress]').value = Math.min(state.subtotal, MINIMUM_ORDER);
      this.querySelector('[data-progress-label]').textContent = state.remaining ? this.money.format(state.remaining / 100) + ' to the $1,000 minimum' : '$1,000 order minimum met';
      const groupError = state.errors.find(error => !error.includes('subtotal'));
      this.querySelector('[data-order-requirements]').textContent = state.valid ? 'Ready to review · minimums met' : groupError || 'Continue building your order.';
      this.querySelector('[data-trade-submit]').disabled = this.busy || !state.valid;
      this.querySelector('[data-clear-selection]').disabled = this.busy || !state.units;
      if (this.dataset.localCheckout) try { sessionStorage.setItem(this.storageKey(), JSON.stringify(Object.fromEntries(this.rows.map(row => [row.dataset.id, this.quantity(row)])))); } catch {}
      return state;
    }
    onClick(event) {
      const button = event.target.closest('button'); if (!button || button.disabled) return;
      const row = button.closest('[data-trade-row]');
      if (button.hasAttribute('data-quantity-plus')) this.setQuantity(row, this.nextQuantity(row));
      else if (button.hasAttribute('data-quantity-minus')) {
        const next = this.quantity(row) - (Number(row.dataset.increment) || 1);
        this.setQuantity(row, next < Math.max(3, Number(row.dataset.min) || 1) ? 0 : next);
      } else if (button.hasAttribute('data-selected-filter')) { this.selectedOnly = !this.selectedOnly; this.filter(); }
      else if (button.hasAttribute('data-clear-filters')) { this.querySelector('[data-trade-search]').value = ''; this.selectedOnly = false; this.filter(); }
      else if (button.hasAttribute('data-clear-selection')) {
        for (const r of this.rows) { r.querySelector('input').dataset.committed = 0; r.querySelector('input').value = 0; r.querySelector('input').removeAttribute('aria-invalid'); }
        this.status.textContent = ''; this.update(); this.filter();
      } else if (button.hasAttribute('data-trade-submit')) this.review();
      else if (button.hasAttribute('data-close-review')) this.dialog.close();
      else if (button.hasAttribute('data-preview-checkout')) this.checkout();
      else if (button.hasAttribute('data-native-checkout')) this.nativeCheckout();
    }
    review() {
      if (!this.update().valid) return;
      const container = this.querySelector('[data-review-lines]'); container.replaceChildren();
      for (const line of this.lines().filter(line => line.quantity)) {
        const item = document.createElement('div'), name = document.createElement('span'), detail = document.createElement('small'), total = document.createElement('strong');
        name.textContent = line.name; detail.textContent = line.variant + ' · ' + line.quantity + ' bottles';
        name.append(detail); total.textContent = this.money.format(line.quantity * line.price / 100);
        item.append(name, total); container.append(item);
      }
      this.querySelector('[data-review-total]').textContent = this.querySelector('[data-order-total]').textContent;
      this.querySelector('[data-checkout-status]').textContent = '';
      this.querySelector('[data-preview-checkout]')?.removeAttribute('disabled');
      this.requestKey = crypto.randomUUID();
      this.dialog.showModal();
    }
    async checkout() {
      if (this.busy || !this.dataset.localCheckout || !this.update().valid) return;
      const endpoint = new URL(this.dataset.localCheckout, location.href);
      if (!['127.0.0.1', 'localhost'].includes(location.hostname) || endpoint.origin !== location.origin) return;
      this.busy = true; this.update();
      const button = this.querySelector('[data-preview-checkout]'); button.disabled = true;
      const status = this.querySelector('[data-checkout-status]'); status.textContent = 'Checking local access, inventory and minimums…';
      try {
        const response = await fetch(endpoint, { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({
          requestKey: this.requestKey, items: this.lines().filter(line => line.quantity).map(line => ({id:line.id, quantity:line.quantity})),
          country: 'US', paymentMethod: 'wire'
        }) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Unable to create the preview order.');
        if (result.order?.simulated !== true || result.order.paymentStatus !== 'AWAITING_WIRE' || result.order.fulfillmentStatus !== 'ON_HOLD') throw new Error('The preview did not confirm the expected payment-pending fulfillment hold.');
        status.textContent = result.order.id + ' · Awaiting wire payment · Fulfillment on hold. Order confirmation is not a payment receipt. This local simulation created no Shopify order, reserved no live stock, initiated no payment and triggers no fulfillment. Shipping, tax and bank instructions are not configured.';
      } catch (error) { status.textContent = error.message + ' No live order has been placed.'; button.disabled = false; }
      finally { this.busy = false; this.update(); }
    }
    async nativeCheckout() {
      if (this.busy || this.dataset.orderingEnabled !== 'true' || this.dataset.localCheckout || !this.dataset.companyLocation || !this.update().valid) return;
      const button = this.querySelector('[data-native-checkout]');
      const destination = new URL(button.dataset.nativeCheckout, location.origin);
      if (destination.origin !== location.origin) return;
      this.busy = true; button.disabled = true; this.update();
      const status = this.querySelector('[data-checkout-status]');
      status.textContent = 'Verifying your selected bottles, company catalog and Shopify bag…';
      try {
        await prepareNativeWholesaleCart({ lines: this.lines().filter(line => line.quantity), cartUrl: this.dataset.cartUrl, addUrl: this.dataset.cartAddUrl, updateUrl: this.dataset.cartUpdateUrl, locationId: this.dataset.companyLocation, origin: location.origin, attemptId: crypto.randomUUID() });
        status.textContent = 'Shopify bag verified. Opening checkout to confirm shipping, tax and wire payment…';
        location.assign(destination.href);
      } catch (error) { status.textContent = error.message; this.busy = false; button.disabled = false; this.update(); }
    }
  });
}
